import * as React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import { MdOutlineAnalytics } from 'react-icons/md';
import { ChevronForwardOutline } from 'react-ionicons';
import { ChevronBackOutline } from 'react-ionicons';
import { LuFileText } from 'react-icons/lu';
import { LuFileCheck } from 'react-icons/lu';
import { PiCurrencyCircleDollar } from 'react-icons/pi';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { GoPerson } from 'react-icons/go';
import { MdOutlineMedicalServices } from 'react-icons/md';
import { MdHistoryEdu } from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoSettingsOutline } from 'react-icons/io5';
import { LuPackage } from 'react-icons/lu';
import { PiBlueprint } from 'react-icons/pi';
import { BsChatLeftText } from 'react-icons/bs';
import { RiMailSendFill } from 'react-icons/ri';
import { MdCampaign } from 'react-icons/md';
import { MdOutlineQuickreply } from 'react-icons/md';
import { TbLogs } from 'react-icons/tb';
import { AiOutlineDashboard } from 'react-icons/ai';
import { RiSurveyLine } from 'react-icons/ri';
import { FiUsers } from 'react-icons/fi';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
// Color palette que coincide con los KPI cards y la tabla
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  purple: '#673ab7',
  background: '#f7f9fc',
  lightBorder: '#e3f2fd',
  hoverRow: alpha('#2196f3', 0.08)
};

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: '4px 8px',
  '&.Mui-selected': {
    backgroundColor: alpha(COLORS.primary, 0.12),
    color: COLORS.primary,
    '&:hover': {
      backgroundColor: alpha(COLORS.primary, 0.15),
    },
    '& .MuiListItemIcon-root': {
      color: COLORS.primary,
    }
  },
  '&:hover': {
    backgroundColor: alpha(COLORS.primary, 0.08),
  }
}));

const MenuSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: alpha('#424242', 0.7),
  padding: '16px 16px 8px 16px',
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: 40,
  color: '#757575',
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiTypography-root': {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
}));

// Elementos del menú principal - disponibles según el rol y tenant_id
const getMainListItems = (tenant_id) => {

  const landingPageItem = { 
    text: 'Landing Page', 
    icon: <HomeIcon fontSize="small" />, 
    link: '/',
    requiredRoles: ['Agente', 'Supervisor', 'Administrador']
  };

  // Dashboard item - visible for tenant_id 1 and others except 3, 5
  const dashboardItem = { 
    text: 'Dashboard', 
    icon: <DashboardIcon fontSize="small" />, 
    link: '/dashboard',
    requiredRoles: ['Supervisor', 'Administrador']
  };

  const mensajesItem = { 
    text: 'Mensajes', 
    icon: <WhatsAppIcon fontSize="small" />, 
    link: 'chats',
    requiredRoles: ['Agente', 'Supervisor', 'Administrador'] 
  };

  // Marketing Dashboard - only for tenant_id 3
  const marketingDashboardItem = { 
    text: 'Dashboard', 
    icon: <AiOutlineDashboard size={20} />, 
    link: '/marketing-dashboard',
    requiredRoles: ['Supervisor', 'Administrador']
  };

  // Logs - only for tenant_id 3
  const logsItem = { 
    text: 'Logs', 
    icon: <TbLogs size={20} />, 
    link: 'logs',
    requiredRoles: ['Supervisor', 'Administrador'] 
  };

  // Marketing - only for tenant_id 3
  const marketingItem = { 
    text: 'Campañas', 
    icon: <MdCampaign size={20} />, 
    link: 'marketing',
    requiredRoles: ['Supervisor', 'Administrador'] 
  };

  // Formularios - only for tenant_id 3
  const formulariosItem = { 
    text: 'Formularios', 
    icon: <RiSurveyLine size={20} />, 
    link: 'formularios',
    requiredRoles: ['Supervisor', 'Administrador'] 
  };

  // Add items based on tenant_id
  if (tenant_id === "1") {
    return [dashboardItem, mensajesItem];
  } else if (tenant_id === "3") {
    return [marketingDashboardItem, marketingItem, formulariosItem, mensajesItem];
  } else if (tenant_id === "5") {
    return [landingPageItem, dashboardItem, mensajesItem];
  } else {
    // Default for other tenant_ids
    return [dashboardItem, mensajesItem];
  }
};

// Elementos del menú de configuración - disponibles según el rol
const secondaryListItems = [
  { 
    text: 'Usuarios', 
    icon: <FiUsers size={20} />, 
    link: 'tenant-users',
    requiredRoles: ['Administrador'] 
  },
];

export default function MenuContent(props) {
  const { profile } = props;
  const tenant_id = profile?.tenant_id;
  const [mainSelected, setMainSelected] = React.useState(0);
  const [acereySelected, setAcereySelected] = React.useState(0);
  const [secondarySelected, setSecondarySelected] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Obtener el rol del usuario del perfil
  const userRole = profile?.role || 'Agente'; // Valor predeterminado si no hay rol
  
  // Obtener los elementos del menú según el tenant_id
  const mainListItems = getMainListItems(tenant_id);
  
  // Filtrar elementos del menú según el rol del usuario
  const filteredMainItems = mainListItems.filter(item => 
    item.requiredRoles.includes(userRole)
  );
  
  const filteredSecondaryItems = secondaryListItems.filter(item => 
    item.requiredRoles.includes(userRole)
  );
  
  React.useEffect(() => {
    const { pathname } = location;
    // Usar los elementos filtrados para encontrar el índice seleccionado
    const mainIndex = filteredMainItems.findIndex(item => item.link === pathname);
    if (mainIndex !== -1) {
      setMainSelected(mainIndex);
    }
    const secondaryIndex = filteredSecondaryItems.findIndex(
      item => item.link === pathname,
    );
    if (secondaryIndex !== -1) {
      setSecondarySelected(secondaryIndex);
    }
  }, [location, filteredMainItems, filteredSecondaryItems]);

  return (
    <Box sx={{ flexGrow: 1, p: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <Box>
        <MenuSectionTitle>Principal</MenuSectionTitle>
        <List sx={{ px: 0 }}>
          {filteredMainItems.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ display: 'block' }}>
              <StyledListItemButton
                selected={index === mainSelected}
                onClick={() => {
                  navigate(item.link);
                  setMainSelected(index);
                  setSecondarySelected(null);
                }}>
                <StyledListItemIcon>{item.icon}</StyledListItemIcon>
                <StyledListItemText primary={item.text} />
              </StyledListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Solo mostrar la sección de configuración si hay elementos para el rol del usuario */}
      {filteredSecondaryItems.length > 0 && (
        <Box>
          <MenuSectionTitle>Configuración</MenuSectionTitle>
          <List sx={{ px: 0 }}>
            {filteredSecondaryItems.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                <StyledListItemButton
                  disabled={item.disabled}
                  selected={index === secondarySelected}
                  onClick={() => {
                    navigate(item.link);
                    setSecondarySelected(index);
                    setMainSelected(null);
                  }}>
                  <StyledListItemIcon>{item.icon}</StyledListItemIcon>
                  <StyledListItemText primary={item.text} />
                </StyledListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
